import React, { useRef, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { ArrowRight, Zap, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

const CTASection = styled.section`
  padding: 8rem 0 6rem;
  background: #ffffff;
  color: #1f2937;
  position: relative;
  overflow: hidden;

  @media (min-width: 768px) {
    padding: 10rem 0;
  }
`;

const CTAContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.5rem;
  position: relative;
  z-index: 10;

  @media (min-width: 1024px) {
    padding: 0 2rem;
  }
`;

const CTAContent = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  transform: ${({ isVisible }) =>
    isVisible ? 'translateY(0)' : 'translateY(30px)'};
  transition: all 0.8s ease;
`;

const CTATitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 900;
  margin-bottom: 1.5rem;
  letter-spacing: -0.025em;
  color: #1f2937;
  background: linear-gradient(45deg, #1f2937, #4b5563);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (min-width: 640px) {
    font-size: 3rem;
  }

  @media (min-width: 768px) {
    font-size: 4rem;
    margin-bottom: 2rem;
  }
`;

const CTADescription = styled.p`
  font-size: 1.25rem;
  color: #4b5563;
  margin-bottom: 3rem;
  line-height: 1.6;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;

  @media (min-width: 640px) {
    font-size: 1.375rem;
  }

  @media (min-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 4rem;
  }
`;

const CTAButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
  margin-bottom: 4rem;

  @media (min-width: 640px) {
    flex-direction: row;
    justify-content: center;
    gap: 1.5rem;
  }
`;

const CTAButton = styled.a`
  background: linear-gradient(45deg, #7c3aed, #ec4899);
  color: white;
  padding: 1rem 2.5rem;
  border-radius: 50px;
  font-weight: 700;
  font-size: 1.125rem;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px rgba(124, 58, 237, 0.3);
  border: none;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-3px) scale(1.05);
    box-shadow: 0 20px 40px rgba(124, 58, 237, 0.4);
  }

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    background: linear-gradient(
      45deg,
      rgba(124, 58, 237, 0.1),
      rgba(236, 72, 153, 0.1)
    );
    border-radius: 50%;
    transition: all 0.6s ease;
    transform: translate(-50%, -50%);
    z-index: -1;
  }

  &:hover::before {
    width: 300px;
    height: 300px;
  }

  svg {
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: translateX(3px);
  }

  @media (min-width: 640px) {
    padding: 1.25rem 3rem;
    font-size: 1.25rem;
  }
`;

const SecondaryButton = styled.a`
  background: transparent;
  color: #4b5563;
  padding: 1rem 2.5rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.125rem;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.3s ease;
  border: 2px solid #6b7280;

  &:hover {
    background: rgba(107, 114, 128, 0.1);
    border-color: #4b5563;
    transform: translateY(-2px);
  }

  @media (min-width: 640px) {
    padding: 1.25rem 3rem;
    font-size: 1.25rem;
  }
`;

const FloatingIcon = styled.div`
  position: absolute;
  color: rgba(124, 58, 237, 0.1);
  font-size: 2rem;
  animation: floatIcon 8s ease-in-out infinite;

  &:nth-of-type(1) {
    top: 20%;
    left: 10%;
    animation-delay: 0s;
  }

  &:nth-of-type(2) {
    top: 30%;
    right: 15%;
    animation-delay: 2s;
  }

  &:nth-of-type(3) {
    bottom: 25%;
    left: 15%;
    animation-delay: 4s;
  }

  @keyframes floatIcon {
    0%,
    100% {
      transform: translateY(0px) rotate(0deg);
      opacity: 0.1;
    }
    50% {
      transform: translateY(-20px) rotate(180deg);
      opacity: 0.2;
    }
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

export default function CTA() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 },
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <CTASection ref={sectionRef}>
      <FloatingIcon>
        <Zap size={40} />
      </FloatingIcon>
      <FloatingIcon>
        <Users size={35} />
      </FloatingIcon>
      <FloatingIcon>
        <ArrowRight size={30} />
      </FloatingIcon>

      <CTAContainer>
        <CTAContent isVisible={isVisible}>
          <CTATitle>Ready to Qraft Your Code?</CTATitle>
          <CTADescription>
            Join thousands of businesses and creators who trust Qraft for their
            QR code needs. Start creating professional QR codes in seconds.
          </CTADescription>

          <CTAButtonGroup>
            <CTAButton as={Link} to="/qr">
              Create Your First QR Code
              <ArrowRight size={20} />
            </CTAButton>

            <SecondaryButton href="#features">
              Learn More
              <ArrowRight size={18} />
            </SecondaryButton>
          </CTAButtonGroup>
        </CTAContent>
      </CTAContainer>
    </CTASection>
  );
}
